import { CONTACT_CONFIG, CONTACT_DETAILS } from '../config/constants.js'
import { PAGE_TITLE as PUBLIC_TITLES } from './workshop-packages.js'
import nbAgency from '../i18n/locales/nb/ai-agency.json' with { type: 'json' }
import enAgency from '../i18n/locales/en/ai-agency.json' with { type: 'json' }

/* ── SEO ───────────────────────────────────────────────────────────── */

export const PAGE_TITLE = PUBLIC_TITLES.agency.nb
export const PAGE_DESCRIPTION = nbAgency.meta.description
export const PAGE_KEYWORDS = nbAgency.meta.keywords

export const AI_AGENCY_PAGE_PATH = '/ai-agency'
export const PAGE_URL = `${CONTACT_CONFIG.WEBSITE_URL}${AI_AGENCY_PAGE_PATH}`
export const PAGE_OG_IMAGE = `${CONTACT_CONFIG.WEBSITE_URL}/farzad-bayat.jpg`
export const CONTACT_PORTRAIT_SRC = '/farzad-bayat.jpg'
export const CONTACT_WEBSITE_DISPLAY = 'farzadbayat.com/ai-agency'

/* ── Types ─────────────────────────────────────────────────────────── */

export interface NavLink {
  label: string
  href: string
}

export interface HeroHighlight {
  id: string
  title: string
  description: string
}

export interface ProofItem {
  stat: string
  label: string
  body: string
}

export interface SectionIntro {
  number: string
  title: string
  copy?: string
}

export interface ServiceCard {
  title: string
  description: string
  outcomes: string[]
  href?: string
}

export interface IndustryModule {
  title: string
  description: string
  useCases: string[]
}

export interface FaqItem {
  question: string
  answer: string
}

export interface AgentPlatform {
  name: string
  tagline: string
  description: string
  deploy: string
  bestFor: string[]
  features: string[]
  url: string
}

/* ── Page copy ──────────────────────────────────────────────────────
 * One source: src/i18n/locales/{nb,en}/ai-agency.json.
 * The app and scripts/prerender-ai-agency.ts both read it. Do not copy text here.
 */

export type AgencyLang = 'nb' | 'en'
export type AgencyContent = typeof nbAgency

export function agencyContent(lang: AgencyLang): AgencyContent {
  return (lang === 'nb' ? nbAgency : enAgency) as AgencyContent
}

const NB = agencyContent('nb')

export const SERVICES: ServiceCard[] = NB.services
export const INDUSTRIES: IndustryModule[] = NB.industries
export const PROCESS_STEPS = NB.processSteps
export const FAQS: FaqItem[] = NB.faqs
export const AGENT_PLATFORMS: AgentPlatform[] = NB.agentPlatforms

/* ── Contact helpers ───────────────────────────────────────────────── */

export const phone = CONTACT_DETAILS.find(c => c.label === 'Phone')?.value ?? '+47 94 44 64 46'
export const email =
  CONTACT_DETAILS.find(c => c.label === 'Email')?.value ?? CONTACT_CONFIG.SUPPORT_EMAIL
export const location = CONTACT_DETAILS.find(c => c.label === 'Location')?.value ?? 'Oslo, Norway'
export const phoneTel =
  CONTACT_DETAILS.find(c => c.label === 'Phone')?.href ?? 'tel:+4794446446'
export const bookingUrl = CONTACT_CONFIG.SCHEDULING.BOOKING_URL

export const NAV_LINKS: NavLink[] = NB.nav.links
export const HEADER_NAV_LINKS: NavLink[] = NB.nav.headerLinks
export const HERO = NB.hero
export const HERO_HIGHLIGHTS: HeroHighlight[] = NB.heroHighlights
export const TRUST_STATS = NB.trustStats
export const SERVICES_INTRO: SectionIntro = NB.servicesIntro
export const INDUSTRIES_INTRO: SectionIntro = NB.industriesIntro
export const AGENTS_INTRO: SectionIntro = NB.agentsIntro
export const AGENTS_CTA = NB.agentsCta
export const PROCESS_INTRO: SectionIntro = NB.processIntro
export const FORK_SECTION: SectionIntro = NB.forkSection
export const PROOF_SECTION: SectionIntro & { disclaimer: string; items: ProofItem[] } = NB.proofSection
export const FAQ_INTRO: SectionIntro = NB.faqIntro
export const CONTACT = NB.contact

export interface ContactLink {
  label: string
  value: string
  href: string
}

export const CONTACT_LINKS: ContactLink[] = NB.contactLinks
export const FOOTER_LINKS: NavLink[] = NB.footerLinks

/* ── JSON-LD structured data helpers ───────────────────────────────── */

export function buildAiAgencyStructuredData(lang: AgencyLang = 'nb') {
  const c = agencyContent(lang)
  const siteUrl = CONTACT_CONFIG.WEBSITE_URL
  const pageUrl = PAGE_URL

  const localBusiness = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: lang === 'nb' ? 'F.B/c Lab — KI-konsulent i Oslo' : 'F.B/c Lab — AI consultant in Oslo',
    image: `${siteUrl}/farzad-bayat.jpg`,
    url: pageUrl,
    telephone: phone,
    email: email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Oslo',
      addressCountry: 'NO',
    },
    areaServed: {
      '@type': 'Country',
      name: lang === 'nb' ? 'Norge' : 'Norway',
    },
    serviceType: lang === 'nb' ? 'KI-konsulent, implementering og egen F.B/c-løsning' : 'AI consulting, implementation, and your own F.B/c solution',
    knowsAbout: [
      'Kunstig intelligens',
      'Maskinlæring',
      'AI-strategi',
      'AI-konsulent',
      'AI-implementering',
      'NLP',
      'Computer Vision',
      'Prosessautomatisering',
      'AI Governance',
      'Digital transformasjon',
    ],
  }

  const services = c.services.map((s, i) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: s.title,
    provider: { '@type': 'ProfessionalService', name: 'F.B/c Lab' },
    areaServed: { '@type': 'Country', name: lang === 'nb' ? 'Norge' : 'Norway' },
    description: s.description,
    url: `${pageUrl}#tjenester`,
    identifier: `service-${i + 1}`,
  }))

  const faqPage = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: c.faqs.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  }

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: lang === 'nb' ? 'Hjem' : 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: lang === 'nb' ? 'KI-konsulenttjenester' : 'AI consulting services',
        item: pageUrl,
      },
    ],
  }

  return [localBusiness, ...services, faqPage, breadcrumb]
}

/** @deprecated Use buildAiAgencyStructuredData */
export const buildStructuredData = buildAiAgencyStructuredData
